/**
 * WHAT: DAMA PRO main container -- Data-Aware Targeted User Access Review module.
 * WHY: Extends standard UAR with MDM/data governance integration, enabling targeted
 *      access reviews based on data sensitivity classifications so organizations
 *      can prioritize attestation of users with access to critical data.
 */

import React, { useState } from 'react';
import ProDashboard from './pro/ProDashboard';
import ProConnections from './pro/ProConnections';
import ProDataSources from './pro/ProDataSources';
import ProClassifications from './pro/ProClassifications';
import ProScopes from './pro/ProScopes';
import ProUserExposure from './pro/ProUserExposure';

/**
 * WHAT: Tabbed navigation wrapper for all DAMA PRO sub-features (dashboard,
 *       connections, data sources, classifications, scopes, user exposure).
 * WHY: Provides a unified interface for the data-aware access review workflow,
 *      from MDM connection setup through sensitivity-based user scoping.
 */
const DamaPro = () => {
    const [activeTab, setActiveTab] = useState('dashboard');

    const tabs = [
        { id: 'dashboard', label: 'Dashboard', icon: 'fa-tachometer-alt' },
        { id: 'connections', label: 'MDM Connections', icon: 'fa-plug' },
        { id: 'sources', label: 'Data Sources', icon: 'fa-database' },
        { id: 'classifications', label: 'Classifications', icon: 'fa-tags' },
        { id: 'scopes', label: 'Targeted Scopes', icon: 'fa-crosshairs' },
        { id: 'exposure', label: 'User Exposure', icon: 'fa-user-shield' },
    ];

    /* WHAT: Returns the sub-feature component corresponding to the currently selected tab.
       WHY: Lazy-switches between PRO sub-features so only the active tab's component is mounted at a time. */
    const renderActiveTab = () => {
        switch (activeTab) {
            case 'dashboard':
                return <ProDashboard />;
            case 'connections':
                return <ProConnections />;
            case 'sources':
                return <ProDataSources />;
            case 'classifications':
                return <ProClassifications />;
            case 'scopes':
                return <ProScopes />;
            case 'exposure':
                return <ProUserExposure />;
            default:
                return <ProDashboard />;
        }
    };

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="mb-1">
                        <i className="fas fa-shield-alt me-2 text-primary"></i>
                        DAMA PRO
                    </h2>
                    <p className="text-muted mb-0">
                        Data-Aware Targeted User Access Review
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <span className="badge bg-info">
                        <i className="fas fa-info-circle me-1"></i>
                        MDM Integration
                    </span>
                </div>
            </div>

            {/* Navigation Tabs */}
            <ul className="nav nav-tabs mb-4">
                {tabs.map(tab => (
                    <li className="nav-item" key={tab.id}>
                        <button
                            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <i className={`fas ${tab.icon} me-2`}></i>
                            {tab.label}
                        </button>
                    </li>
                ))}
            </ul>

            {/* Tab Content */}
            <div className="tab-content">
                {renderActiveTab()}
            </div>
        </div>
    );
};

export default DamaPro;
